import React, { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import type { NewDeliverableInput } from '../../contexts/PortalContext';
import { DELIVERABLE_STATUSES, DELIVERABLE_TYPES, VIDEO_FORMATS } from '../../types/portal';
import { isVersioned, linkFieldsFor, statusOptionLabel } from '../../utils/deliverables';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
import { TranscriptUpload } from './TranscriptUpload';

/** New work always begins planned, with no version until it reaches review. */
const EMPTY: NewDeliverableInput = {
  name: '',
  type: 'Video',
  status: 'Planned',
  version: '',
  driveUrl: '',
  audioUrl: '',
  visibleToClient: false,
  videoFormat: '16:9 Landscape',
  transcript: ''
};
export function AddDeliverable() {
  const {
    addDeliverable,
    notify
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState<NewDeliverableInput>(EMPTY);
  const links = linkFieldsFor(input.type);
  const versioned = isVersioned(input.status);
  function edit(patch: Partial<NewDeliverableInput>) {
    setInput(current => ({
      ...current,
      ...patch
    }));
  }
  function submit(event: React.FormEvent) {
    event.preventDefault();
    const name = input.name.trim();
    if (!name) return;
    /** Versions only exist once the status reaches Review or Final Delivery. */
    addDeliverable({
      ...input,
      name,
      version: versioned ? input.version : ''
    });
    notify(`${name} added`);
    setInput(EMPTY);
    setOpen(false);
  }
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-8 inline-flex items-center gap-3`} data-id="element-136" data-mp="1hg9eg40gm0cg">
        <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-137" data-mp="1cirgt9rw77jr" />
        Add Deliverable
      </button>;
  }
  return <form onSubmit={submit} className="mt-8 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-138" data-mp="1lhcxezdt0chw">
      <input value={input.name} onChange={event => edit({
      name: event.target.value
    })} placeholder="e.g. Speaker Interview 01" aria-label="Deliverable title" autoFocus className="w-full border-0 border-b border-paper/25 bg-transparent pb-3 font-display text-3xl leading-none text-paper placeholder:text-paper/30 focus:border-paper focus:outline-none sm:text-4xl" data-id="element-139" data-mp="1lfi878vi1y80" />

      <div className="mt-9 grid grid-cols-1 gap-8 sm:grid-cols-2" data-id="element-140" data-mp="11uokjs04ncu3">
        <label className="block" data-id="element-141" data-mp="11wivqelljqkz">
          <span className="label block text-paper/40" data-id="element-142" data-mp="16szwc89zfxey">Type</span>
          <select value={input.type} onChange={event => edit({
          type: event.target.value as NewDeliverableInput['type']
        })} className={SELECT} data-id="element-143" data-mp="16vm2c3h864v2">
            {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-144" data-mp="3dkqydkc0juu9">
                {option}
              </option>)}
          </select>
        </label>

        <label className="block" data-id="element-145" data-mp="3hndrdokf7lt1">
          <span className="label block text-paper/40" data-id="element-146" data-mp="3a2bakdth8yux">Status</span>
          <select value={input.status} onChange={event => edit({
          status: event.target.value as NewDeliverableInput['status']
        })} className={SELECT} data-id="element-147" data-mp="35bbf7d00aouj">
            {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-148" data-mp="2w8k278rhfl0s">
                {statusOptionLabel(option)}
              </option>)}
          </select>
        </label>

        {input.type === 'Video' && <label className="block" data-id="element-149" data-mp="3k07nnb5z63vo">
            <span className="label block text-paper/40" data-id="element-150" data-mp="3jz6b129b99bu">Video Format</span>
            <select value={input.videoFormat ?? '16:9 Landscape'} onChange={event => edit({
          videoFormat: event.target.value as NewDeliverableInput['videoFormat']
        })} className={SELECT} data-id="element-151" data-mp="3f1nx0xgfs3wz">
              {VIDEO_FORMATS.map(option => <option key={option} value={option} className={OPTION} data-id="element-152" data-mp="3rhm7w5zgq84i">
                  {option}
                </option>)}
            </select>
          </label>}

        {versioned && <label className="block" data-id="element-153" data-mp="3ijk27d4saraw">
            <span className="label block text-paper/40" data-id="element-154" data-mp="3mjkg8wnz10hf">Version</span>
            <input value={input.version} onChange={event => edit({
          version: event.target.value
        })} placeholder="e.g. V1" className={FIELD} data-id="element-155" data-mp="3mju86pskthpr" />
          </label>}
      </div>

      {input.type === 'Transcript' && <TranscriptUpload value={input.transcript ?? ''} onChange={text => edit({
      transcript: text
    })} data-id="element-156" data-mp="52krew159ki7" />}

      {links.drive && <label className="mt-9 block" data-id="element-157" data-mp="2pt212hnmf004">
          <span className="label block text-paper/40" data-id="element-158" data-mp="2ps0fhj8zav45">{links.driveLabel}</span>
          <input value={input.driveUrl} onChange={event => edit({
        driveUrl: event.target.value
      })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-159" data-mp="2ut66ncmzqabk" />
        </label>}

      {links.audio && <label className="mt-9 block" data-id="element-160" data-mp="2wa3gzn0s07u4">
          <span className="label block text-paper/40" data-id="element-161" data-mp="2w8sh3yagbk8a">Audio Review Link</span>
          <input value={input.audioUrl} onChange={event => edit({
        audioUrl: event.target.value
      })} placeholder="Paste Drive audio link" className={FIELD} data-id="element-162" data-mp="2rbigdmhm3m55" />
        </label>}

      <label className="mt-9 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-163" data-mp="1yjqjw9kzd0rf">
        <span className="label text-paper/40" data-id="element-164" data-mp="2dciv8otb5pdq">Shown in Client Portal</span>
        <button type="button" onClick={() => edit({
        visibleToClient: !input.visibleToClient
      })} aria-pressed={input.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', input.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-165" data-mp="28iej7r56qr5s">
          {input.visibleToClient ? 'On' : 'Off'}
        </button>
      </label>

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-166" data-mp="3toyjaf07m70d">
        <button type="submit" className={BUTTON_SOLID} data-id="element-167" data-mp="3toggezj1kujh">
          Add Deliverable
        </button>
        <button type="button" onClick={() => {
        setInput(EMPTY);
        setOpen(false);
      }} className={BUTTON_QUIET} data-id="element-168" data-mp="2x0a3lpbyuvu4">
          Cancel
        </button>
      </div>
    </form>;
}